/* Lightweight markdown renderer for KAI chat replies.
   Handles **bold**, *italic*, `inline code`, # headings, - bullet lists, 1. ordered lists,
   and paragraphs. Intentionally minimal — no library, no XSS risk because we never
   set innerHTML; we render React nodes from parsed segments. */

import { Fragment } from "react";

/* Parse inline markdown ( **bold**, *italic*, `code` ) into React nodes. */
const parseInline = (text) => {
  // Tokenize by matching bold, italic, code segments in order
  const out = [];
  let rest = text;
  let key = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`)/;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const m = rest.match(re);
    if (!m) {
      if (rest) out.push(<Fragment key={`t-${key++}`}>{rest}</Fragment>);
      break;
    }
    if (m.index > 0) out.push(<Fragment key={`t-${key++}`}>{rest.slice(0, m.index)}</Fragment>);
    if (m[2] !== undefined)      out.push(<strong key={`b-${key++}`} className="font-bold">{m[2]}</strong>);
    else if (m[3] !== undefined) out.push(<em key={`i-${key++}`} className="italic">{m[3]}</em>);
    else if (m[4] !== undefined) out.push(<code key={`c-${key++}`} className="rounded bg-muted/60 px-1 py-0.5 font-mono text-[12px]">{m[4]}</code>);
    rest = rest.slice(m.index + m[0].length);
  }
  return out;
};

export const KaiMarkdown = ({ text = "" }) => {
  // Block parse: split into lines, group bullets, paragraphs, headings.
  const lines = text.split("\n");
  const blocks = [];
  let buffer = [];
  let mode = null; // 'p' | 'ul' | 'ol'

  const flush = () => {
    if (!buffer.length) return;
    if (mode === "ul") {
      blocks.push(
        <ul key={blocks.length} className="my-2 list-disc space-y-1 pl-5">
          {buffer.map((l, i) => <li key={i}>{parseInline(l)}</li>)}
        </ul>,
      );
    } else if (mode === "ol") {
      blocks.push(
        <ol key={blocks.length} className="my-2 list-decimal space-y-1 pl-5">
          {buffer.map((l, i) => <li key={i}>{parseInline(l)}</li>)}
        </ol>,
      );
    } else {
      blocks.push(
        <p key={blocks.length} className="my-1.5 first:mt-0 last:mb-0">
          {buffer.map((l, i) => (
            <Fragment key={i}>
              {parseInline(l)}
              {i < buffer.length - 1 && <br />}
            </Fragment>
          ))}
        </p>,
      );
    }
    buffer = [];
    mode = null;
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flush(); continue; }

    // Heading
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      flush();
      const level = h[1].length;
      const sizes = ["text-base", "text-base", "text-[15px]", "text-[14px]", "text-[13px]", "text-[12px]"];
      blocks.push(
        <p key={blocks.length} className={`mt-2.5 mb-1.5 font-display font-black tracking-tight first:mt-0 ${sizes[level - 1]}`}>
          {parseInline(h[2])}
        </p>,
      );
      continue;
    }

    // Bullet list
    const bullet = line.match(/^[-•*]\s+(.*)$/);
    if (bullet) {
      if (mode !== "ul") { flush(); mode = "ul"; }
      buffer.push(bullet[1]);
      continue;
    }

    // Ordered list
    const ordered = line.match(/^\d+\.\s+(.*)$/);
    if (ordered) {
      if (mode !== "ol") { flush(); mode = "ol"; }
      buffer.push(ordered[1]);
      continue;
    }

    // Plain paragraph line
    if (mode !== "p") { flush(); mode = "p"; }
    buffer.push(line);
  }
  flush();

  return <div className="kai-md">{blocks}</div>;
};
